import { Grid, Stack, Typography } from "@mui/material";
import { FormikDecimalTextField } from "@shared";
import { FormikProps } from "formik";

import { SeizableIncomePeriodResponse } from "@/api";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikCheckbox } from "@/common/components/fields/formik/formik-checkbox/FormikCheckbox";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { SeizableIncomeCalculatorFormValues } from "../SeizableIncomeCalculatorDialog.types";
import {
  EXECUTION_TYPE_MAINTENANCE,
  TRANSLATION_BASE,
  buildExecutionTypeOptions,
  buildPeriodOptions,
} from "../SeizableIncomeCalculatorDialog.utils";

interface SeizableIncomeInputsProps {
  formik: FormikProps<SeizableIncomeCalculatorFormValues>;
  availablePeriods: SeizableIncomePeriodResponse[];
}

export function SeizableIncomeInputs({ formik, availablePeriods }: SeizableIncomeInputsProps) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const isMaintenanceInput = formik.values.executionType === EXECUTION_TYPE_MAINTENANCE;

  return (
    <Stack flex={1} gap={3} py={2}>
      <Typography variant="overline" color="text.secondary">
        {t(`${TRANSLATION_BASE}.inputsTitle`)}
      </Typography>

      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={12}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="period"
            label={t(`${TRANSLATION_BASE}.fields.period`)}
            options={buildPeriodOptions(availablePeriods, t, locales[language])}
          />
        </Grid>
        <Grid size={12}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="executionType"
            label={t(`${TRANSLATION_BASE}.fields.executionType`)}
            options={buildExecutionTypeOptions(t)}
          />
        </Grid>
        <Grid size={12}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="monthlyIncome"
            label={t(`${TRANSLATION_BASE}.fields.monthlyIncome`)}
            minValue={0}
            required
          />
        </Grid>
        <Grid size={12}>
          <FormikCheckbox
            formik={formik}
            fieldName="hasSpecialPayments"
            label={t(`${TRANSLATION_BASE}.fields.hasSpecialPayments`)}
            helperText={t(`${TRANSLATION_BASE}.fields.hasSpecialPaymentsHint`)}
          />
        </Grid>
        <Grid size={12}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="maintenanceObligationCount"
            label={t(`${TRANSLATION_BASE}.fields.maintenanceObligationCount`)}
            decimalScale={0}
            minValue={0}
          />
        </Grid>
        {isMaintenanceInput && (
          <Grid size={12}>
            <FormikDecimalTextField
              formik={formik}
              fieldName="maintenanceSeizureCount"
              label={t(`${TRANSLATION_BASE}.fields.maintenanceSeizureCount`)}
              decimalScale={0}
              minValue={0}
            />
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}
